import { HubSpotForm } from "~/components/forms/HubSpotForm";

/**
 * conference-cta block (1 use)
 *
 * Conference registration CTA with two-column layout.
 * Left side: heading, date, image, description, and optional topics list.
 * Right side: sticky HubSpot form with gradient background.
 * Also includes an optional "about" section below with dark blue background.
 *
 * Hugo template: blocks/conference-cta.html
 */
export function ConferenceCta({
  heading,
  date,
  label_style,
  image,
  subheading,
  copy,
  image_2,
  list,
  list_heading,
  form_heading,
  hubspot_form_id,
  about,
}: {
  heading?: string;
  date?: string;
  label_style?: string;
  image?: string;
  subheading?: string;
  copy?: string;
  image_2?: string;
  list?: string[];
  list_heading?: string;
  form_heading?: string;
  hubspot_form_id?: string;
  about?: {
    heading?: string;
    copy?: string;
  };
}) {
  return (
    <div className="bg-cream">
      <div className="relative px-4 pt-8 pb-16 md:px-8 lg:pt-14 lg:pb-20">
        <div className="relative max-w-screen-xl mx-auto">
          <div className="flex flex-col items-stretch gap-7 lg:gap-16 lg:flex-row">
            {/* Left column */}
            <div className="w-full lg:w-[48%] lg:flex flex-col">
              {heading && (
                <div className="mb-8">
                  <h1 className="inline-block border border-teal-dark/30 bg-teal-dark/10 text-charcoal rounded leading-11 px-5 py-1.5 font-body font-medium text-sm">
                    {heading}
                  </h1>
                </div>
              )}
              {date && (
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-5">
                  <p className="font-body uppercase text-charcoal text-xs tracking-widest-4 font-medium">
                    upcoming
                  </p>
                  <p
                    className={`rounded leading-11 px-5 py-1.5 font-body font-medium text-sm ${
                      label_style === "green"
                        ? "bg-teal-dark/10"
                        : "bg-gray-100/60"
                    } text-gray-80`}
                  >
                    {date}
                  </p>
                </div>
              )}
              {image && (
                <div className="mb-5 lg:mb-8">
                  <img src={image} alt="" className="w-full" loading="lazy" />
                </div>
              )}
              {subheading && (
                <h2 className="font-display text-charcoal font-bold text-25 mb-5">
                  {subheading}
                </h2>
              )}
              {copy && (
                <div
                  className="max-w-none font-body text-gray-80 text-16 leading-relaxed space-y-6 hidden lg:block content [&_h2>a]:hidden"
                  dangerouslySetInnerHTML={{ __html: copy }}
                />
              )}
              {image_2 && (
                <div className="mt-5 lg:mt-8">
                  <img src={image_2} alt="" className="w-full" loading="lazy" />
                </div>
              )}
              {list && list.length > 0 && (
                <ul className="mt-5 lg:mt-8 space-y-4 font-body text-16 leading-none text-charcoal bg-gray-100/25 p-6 rounded-xl hidden lg:block">
                  <li>
                    <p className="font-body uppercase text-charcoal text-xs tracking-widest-4 font-medium">
                      {list_heading || "Topics"}
                    </p>
                  </li>
                  {list.map((item, i) => (
                    <li key={i} className="flex space-x-3 leading-snug">
                      <span>&#10004;</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Right column - Form */}
            <div className="flex-1 lg:sticky lg:top-10 lg:self-start lg:pt-14">
              <div className="bg-teal-dark rounded-xl">
                <div className="p-6 lg:p-8 relative">
                  <p className="mb-4 lg:mb-6 font-body text-white font-semibold text-xxs lg:text-xs uppercase tracking-widest">
                    {form_heading || "Register to attend"}
                  </p>
                  {hubspot_form_id ? (
                    <HubSpotForm formId={hubspot_form_id} />
                  ) : (
                    <div className="rounded-lg border-2 border-dashed border-white/30 p-8 text-center text-white/60">
                      No form configured
                    </div>
                  )}
                </div>
              </div>

              {/* Mobile: copy and list below form */}
              {copy && (
                <div
                  className="max-w-none font-body text-gray-80 text-16 leading-relaxed mt-5 lg:mt-8 space-y-6 lg:hidden content [&_h2>a]:hidden"
                  dangerouslySetInnerHTML={{ __html: copy }}
                />
              )}
              {list && list.length > 0 && (
                <ul className="mt-5 lg:mt-8 space-y-4 font-body text-16 leading-none text-charcoal bg-gray-100/25 p-6 rounded-xl flex-1 lg:hidden">
                  <li>
                    <p className="font-body uppercase text-charcoal text-xs tracking-widest-4 font-medium">
                      {list_heading || "Topics"}
                    </p>
                  </li>
                  {list.map((item, i) => (
                    <li key={i} className="flex space-x-3 leading-snug">
                      <span>&#10004;</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* About section */}
      {about && (
        <div className="relative px-4 pb-14 md:px-8 pt-14 lg:pb-28 bg-[#161415]">
          <div className="relative max-w-screen-xl mx-auto">
            <div className="max-w-3xl">
              {about.heading && (
                <h2 className="font-display text-white font-bold text-31 mb-6">
                  {about.heading}
                </h2>
              )}
              {about.copy && (
                <div
                  className="font-body text-white/70 text-16 leading-relaxed space-y-6"
                  dangerouslySetInnerHTML={{ __html: about.copy }}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
